/**
 * 문자열을 아주 단순하게 토큰(단어) 배열로 바꿔주는 함수
 */
function simpleTokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[\n\r]/g, ' ')
    .replace(/[.,!?;:]/g, '') // 구두점 제거
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * 토큰 배열을 빈도수 맵으로 변환합니다.
 * 각 토큰이 몇 번 등장하는지 카운트합니다.
 *
 * @param tokens 토큰 배열
 * @returns 토큰을 키로, 등장 횟수를 값으로 가지는 객체
 * @example
 * toFreqMap(['안녕', '고객님', '안녕']) // { '안녕': 2, '고객님': 1 }
 */
function toFreqMap(tokens: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const t of tokens) {
    map[t] = (map[t] || 0) + 1;
  }
  return map;
}

/**
 * AI 답변과 최종 답변의 수정률을 계산합니다.
 * 순서는 보지 않고, 추가/삭제된 토큰의 합을 전체 토큰 대비 비율로 계산합니다.
 *
 * @param original AI가 생성한 원본 답변
 * @param edited 사람이 수정한 최종 답변
 * @returns 0~100 정수. 값이 클수록 수정이 많음.
 */
export function calcEditRatePercent(original: string, edited: string): number {
  const origTokens = simpleTokenize(original);
  const editTokens = simpleTokenize(edited);

  // 둘 다 비어 있으면 변경 없음
  if (origTokens.length === 0 && editTokens.length === 0) {
    return 0;
  }

  // 한쪽만 비어 있으면 100% 수정
  if (origTokens.length === 0 || editTokens.length === 0) {
    return 100;
  }

  const origFreq = toFreqMap(origTokens);
  const editFreq = toFreqMap(editTokens);

  // 공통 토큰 개수 계산
  let commonCount = 0;
  for (const token of Object.keys(editFreq)) {
    if (origFreq[token]) {
      commonCount += Math.min(origFreq[token], editFreq[token]);
    }
  }

  // 추가된 토큰 = 최종에는 있지만 공통이 아닌 것
  const added = editTokens.length - commonCount;
  // 삭제된 토큰 = 원본에는 있지만 공통이 아닌 것
  const deleted = origTokens.length - commonCount;

  // 전체 변경량 = 추가 + 삭제
  const totalChanged = added + deleted;
  // 전체 토큰 수 (중복 제거 전)
  const totalTokens = origTokens.length + editTokens.length;

  const changeRate = totalChanged / totalTokens;
  const changeRatePercent = Math.min(changeRate * 100, 100);

  return Math.round(changeRatePercent);
}
